import _, { flatMap, isArray, isEqual, isObject, map, size, sum } from 'lodash';
import {
  MyLeadsSearchQuery,
  MyLeadsFilterTypes,
  myLeadsAttributesBasedTypes,
  SubsectionKeys,
} from '@mm/state/interfaces/myLeads';
import { FilterItem, TagFilterItem } from '@mm/state/model/AudiencesModel';
import { getUniqId } from '@mm/utils';
import { convertToUpperSnakeCase } from '../../GetLeads/LeadsSearch/LeadsSearchFiltersMenu/helpers';
import { MAX_QUERY_ATTRIBUTES_LENGTH } from '../../GetLeads/LeadsSearch/LeadsSearchFiltersMenu/LeadsSearchFiltersMenu';
import { useCallback } from 'react';

export type UpdateFilterParamsType = {
  key: string | number;
  value: string | Record<string, any> | boolean | string[];
  isExisting: boolean;
  type: MyLeadsFilterTypes;
  skipPrevValues?: boolean;
};

export const useFilterParams = ({
  filterParams,
}: {
  filterParams: MyLeadsSearchQuery;
}) => {
  const isFilterParamExist = ({
    key,
    value,
    type = MyLeadsFilterTypes.variables,
  }: {
    key: string;
    value: string | boolean | string[];
    type: MyLeadsFilterTypes;
  }): boolean => {
    const searchKey = myLeadsAttributesBasedTypes.includes(type)
      ? key
      : convertToUpperSnakeCase(key);
    const arr = filterParams.query?.[type]?.[searchKey];
    const isObjectValue = isObject(arr?.[0]);

    const valuesArr: string[] = isObjectValue
      ? Object.values(arr?.[0] || {}).flat()
      : arr || [];

    return (
      !!valuesArr &&
      valuesArr.some(val =>
        Array.isArray(value) ? value.includes(val) : val === value,
      )
    );
  };

  const hasActiveFilters = ({
    keys,
    types,
  }: {
    keys: string[];
    types: MyLeadsFilterTypes[];
  }): boolean =>
    types.some(type => {
      const checkedKeys = myLeadsAttributesBasedTypes.includes(type)
        ? keys
        : keys.map(key => convertToUpperSnakeCase(key));
      const obj = filterParams?.query?.[type] || {};

      return checkedKeys.some(key =>
        Object.prototype.hasOwnProperty.call(obj, key),
      );
    });

  const getUpdatedFilterParams = (
    {
      key,
      value,
      isExisting,
      type = MyLeadsFilterTypes.variables,
      skipPrevValues = false,
    }: UpdateFilterParamsType,
    excludeKeys: string[] = [SubsectionKeys.leadSource],
  ): MyLeadsSearchQuery => {
    const {
      query,
      filterParams: oldFilterParams,
      ...rest
    } = filterParams || {};

    const paramsKey = myLeadsAttributesBasedTypes.includes(type)
      ? key
      : convertToUpperSnakeCase(key.toString());
    const obj = query?.[type] || {};

    const prevValues = !skipPrevValues && obj[paramsKey] ? obj[paramsKey] : [];
    let values: string[] | Record<string, any> = [...prevValues];

    if (isExisting) {
      values = Array.isArray(value)
        ? values.filter(val => !value.includes(val))
        : values.filter(val => val !== value);
    } else if (value) {
      if (Array.isArray(value)) {
        values.push(...value);
      } else {
        values.push(value);
      }
    }

    const updatedQuery = {
      query: {
        ...filterParams?.query,
        [type]: {
          ...obj,
          [paramsKey]: values,
        },
      },
      filterParams: oldFilterParams?.filter((f: FilterItem) =>
        typeof key === 'number'
          ? f.variableId !== Number(key)
          : f.filterType !== key,
      ),
      ...rest,
    };

    // Remove empty nested objects/arrays
    const queryOption = updatedQuery.query[type];
    if (
      queryOption &&
      (Object.keys(queryOption[paramsKey] || {}).length === 0 ||
        (Array.isArray(queryOption[paramsKey]) &&
          queryOption[paramsKey].length === 0))
    ) {
      delete queryOption[paramsKey];
      if (Object.keys(queryOption).length === 0) {
        delete updatedQuery.query[type];
      }
    }

    // Remove any keys listed in excludeKeys from each query type
    if (updatedQuery.query) {
      for (const queryType in updatedQuery.query) {
        if (
          Object.prototype.hasOwnProperty.call(updatedQuery.query, queryType)
        ) {
          excludeKeys.forEach(excludeKey => {
            if (updatedQuery.query[queryType]?.[excludeKey]) {
              delete updatedQuery.query[queryType][excludeKey];
            }
          });

          // Remove query type object if empty after removing excludeKeys
          if (Object.keys(updatedQuery.query[queryType]).length === 0) {
            delete updatedQuery.query[queryType];
          }
        }
      }
    }

    return updatedQuery;
  };

  const getUpdatedVariablesFilterParams = (
    accessor: 'variableId' | 'filterType',
    filter: FilterItem | Pick<FilterItem, 'filterType' | 'value'>,
  ) => {
    const filterItemsArray: FilterItem[] = [];
    filterParams.filterParams
      ?.filter((f: FilterItem) => f[accessor] !== filter?.[accessor])
      .forEach((f: FilterItem) => filterItemsArray.push(f));

    const uniqId = getUniqId('filter');
    const newFilter = filter.value?.length ? { ...filter, uniqId } : undefined;

    const updatedFilterParams = {
      ...filterParams,
      filterParams: [...filterItemsArray, newFilter].filter(Boolean),
    };

    return updatedFilterParams;
  };

  const getUpdatedTagFilters = (values: string[]) => {
    const tagsFilters = filterParams.tagsFilters as TagFilterItem[];
    const updatedFilterParams = {
      ...filterParams,
      tagsFilters: tagsFilters?.filter(
        (filter: TagFilterItem) =>
          !isEqual(
            filter.values.map(v => v.name),
            values,
          ),
      ),
    };

    return updatedFilterParams;
  };

  const getFilterParamsForActiveTab = useCallback(
    (activeTab: string) => {
      const isAllContacts = activeTab === 'all';
      const value =
        activeTab === 'new'
          ? ['New']
          : activeTab === 'existing'
          ? ['Existing']
          : '';
      const updatedQuery = isAllContacts
        ? {}
        : getUpdatedFilterParams(
            {
              key: SubsectionKeys.contactOrigin,
              value,
              isExisting: false,
              type: MyLeadsFilterTypes.attributes,
              skipPrevValues: true,
            },
            [],
          );
      return updatedQuery;
    },
    [getUpdatedFilterParams],
  );

  const countQueryObjLength = (exceptionKeys: string[] = []): number =>
    sum(
      flatMap(filterParams.query, querySection =>
        map(querySection, (value, key) => {
          if (exceptionKeys.includes(key)) return 0;
          if (isArray(value)) return value.length;
          if (isObject(value)) return size(value);
          return 1;
        }),
      ),
    );
  const filtersCount = (exceptionKeys: string[] = []) =>
    countQueryObjLength(exceptionKeys) +
    (filterParams.filterParams?.length || 0) +
    (filterParams?.tagsFilters?.length || 0);
  const hasFilters = filtersCount() > 0;
  const hasFiltersWithoutExceptionKeys =
    filtersCount([SubsectionKeys.leadSource]) > 0;

  const shouldDisableFilters = filtersCount() >= MAX_QUERY_ATTRIBUTES_LENGTH;

  return {
    isFilterParamExist,
    hasActiveFilters,
    getUpdatedFilterParams,
    getFilterParamsForActiveTab,
    getUpdatedVariablesFilterParams,
    getUpdatedTagFilters,
    shouldDisableFilters,
    hasFilters,
    hasFiltersWithoutExceptionKeys,
    filtersCount,
  };
};
